import React, { useState, useEffect, useRef } from 'react';
import { integrationsServiceV2 } from '../../services/integrationsServiceV2';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Plug, Unplug, CheckCircle, XCircle, AlertTriangle, TestTube2, ServerCog, ChevronRight, Power, Key, Database } from 'lucide-react';
import { AndroidLogo, User, Wrench } from '@phosphor-icons/react';
import { ConfirmModal } from '../ui/confirm-modal';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { cn } from '../../lib/utils';

const HighlightedMessage = ({ message = '', isTriggerStep = false }) => {
  // Parse bracketed text from the message for highlighting
  const parts = message.split(/(\[[^\]]+\])/g);
  let highlightIndex = 0;

  // Helper function to get contextual description based on content
  const getContextualDescription = (text, isTrigger) => {
    if (isTrigger) {
      return {
        title: "Trigger",
        description: "This phrase initiates the interaction."
      };
    }

    let infoType = "required detail";
    const lowerText = text.toLowerCase();

    // More robust detection logic
    if (lowerText.includes('card') || /^\d{8,}$/.test(text.replace(/\s/g, ''))) {
      infoType = "account number";
    } else if (lowerText.includes('$') || lowerText.includes('amount') || lowerText.includes('balance')) {
      infoType = "transaction amount";
    } else if (lowerText.includes('email') || lowerText.includes('@')) {
      infoType = "customer's email address";
    } else if (lowerText.includes('phone') || lowerText.includes('(') || lowerText.includes('-')) {
      infoType = "customer's phone number";
    } else if (lowerText.includes('service') || lowerText.includes('wash') || lowerText.includes('fold')) {
      infoType = "service type";
    } else if (/^[A-Z][a-z]+ [A-Z]/.test(text)) {
      infoType = "customer's name";
    }

    return {
      title: "Information Prompt",
      description: `The AI will ask for the ${infoType}.`
    };
  };

  return (
    <TooltipProvider>
      {parts.map((part, i) => {
        // Check if this part is bracketed text
        if (part.startsWith('[') && part.endsWith(']')) {
          // Remove the brackets and apply highlight styling
          const highlightedText = part.slice(1, -1);
          const isTrigger = isTriggerStep && highlightIndex === 0;
          highlightIndex++;

          const { title, description } = getContextualDescription(highlightedText, isTrigger);

          return (
            <Tooltip key={i}>
              <TooltipTrigger asChild>
                <span className="font-medium underline decoration-2 underline-offset-2 decoration-current/60 cursor-help">
                  {highlightedText}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-sm">
                  <div className="font-medium text-gray-900 mb-1">
                    {title}
                  </div>
                  <div className="text-gray-600 leading-relaxed">
                    {description}
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </TooltipProvider>
  );
};

const ToolTestSection = ({ connectionId, tool, capabilityName }) => {
  const [testParameters, setTestParameters] = useState({});
  const [isTesting, setIsTesting] = useState(false);

  const userProvidedParams = tool.integration_tool_parameters.filter(p => p.parameter_source === 'USER_INPUT');
  const requiredParams = userProvidedParams.filter(p => p.required);

  const isFormInvalid = () => {
    for (const param of requiredParams) {
      if (!testParameters[param.name] || testParameters[param.name].trim() === '') {
        return true;
      }
    }
    return false;
  };

  const handleParameterChange = (name, value) => {
    setTestParameters(prev => ({ ...prev, [name]: value }));
  };

  const handleRunTest = async () => {
    if (isTesting) return;

    const missingFields = requiredParams
      .filter(param => !testParameters[param.name] || testParameters[param.name].trim() === '')
      .map(param => param.name);

    if (missingFields.length > 0) {
      toast.error('Missing Required Fields', {
        description: `Please provide a value for: ${missingFields.join(', ')}.`,
      });
      return;
    }

    setIsTesting(true);
    try {
      const result = await integrationsServiceV2.executeTool(connectionId, tool.id, testParameters);
      toast.success(`Test for "${capabilityName}" successful!`, {
        description: <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4"><code className="text-white">{JSON.stringify(result, null, 2)}</code></pre>
      });
    } catch (error) {
      toast.error(`Test for "${capabilityName}" Failed`, { description: error.message });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center space-x-2">
          <TestTube2 className="w-4 h-4 text-gray-500" />
          <h6 className="text-sm font-medium text-gray-700">Test This Tool</h6>
        </div>
      </div>
      <div className="p-4 space-y-4">
        {userProvidedParams.map(param => (
          <div key={param.id} className="space-y-2">
            <Label htmlFor={`test-${param.id}`} className="text-xs font-medium text-gray-600 flex items-center">
              {param.name}
              {param.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={`test-${param.id}`}
              name={param.name}
              type={param.type || 'text'}
              placeholder={param.description}
              value={testParameters[param.name] || ''}
              onChange={(e) => handleParameterChange(param.name, e.target.value)}
              disabled={isTesting}
              className="w-full border-gray-200 rounded-lg h-10 px-3 text-sm focus:ring-1 focus:ring-black/50"
            />
          </div>
        ))}
      </div>
      <div className="px-4 py-3 bg-gray-50/70 flex justify-end">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleRunTest}
          className={cn(
            "py-2 px-4 rounded-lg bg-black text-white text-xs font-medium transition-opacity",
            (isTesting || isFormInvalid()) && "opacity-50 cursor-not-allowed"
          )}
        >
          {isTesting ? "Running..." : "Run Test"}
        </motion.button>
      </div>
    </div>
  );
};

const IntegrationV2Content = ({ selectedItem, onConnect, onDisconnect, isLoading }) => {
  const [formData, setFormData] = useState({});
  const [capabilities, setCapabilities] = useState([]);
  const [isLoadingCapabilities, setIsLoadingCapabilities] = useState(false);
  const [testCardNumber, setTestCardNumber] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [expandedCapability, setExpandedCapability] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false); // Local state for immediate feedback

  const scrollContainerRef = useRef(null);
  const prevStatusRef = useRef(selectedItem.status);

  useEffect(() => {
    // Scroll to top when transitioning from entering credentials to the connected view
    if (
      prevStatusRef.current === 'NOT_CONNECTED' &&
      selectedItem.status === 'CONNECTED' &&
      scrollContainerRef.current
    ) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    prevStatusRef.current = selectedItem.status;
  }, [selectedItem.status]);

  useEffect(() => {
    setFormData({});
    setCapabilities([]);
    setTestCardNumber('');
    setExpandedCapability(null);
    const fetchCapabilities = async () => {
      if (selectedItem.status === 'CONNECTED' && selectedItem.id) {
        setIsLoadingCapabilities(true);
        try {
          const caps = await integrationsServiceV2.getIntegrationCapabilities(selectedItem.id);
          setCapabilities(caps);
        } catch (error) {
          console.error("Failed to fetch integration capabilities:", error);
          toast.error("Could not load integration capabilities.");
        } finally {
          setIsLoadingCapabilities(false);
        }
      }
    };
    fetchCapabilities();
  }, [selectedItem]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleConnectClick = async () => {
    if (isConnecting || isLoading) return; // Prevent spamming
    setIsConnecting(true);
    try {
      await onConnect(selectedItem.id, formData);
    } finally {
      setIsConnecting(false);
    }
  };
  const handleDisconnectClick = async () => {
    setIsConfirmModalOpen(false);
    onDisconnect(selectedItem.connection.id);
  };

  const handleTestConnection = async () => {
    if (!testCardNumber) {
      toast.error("Please enter a card number to run the test.");
      return;
    }
    setIsTesting(true);
    try {
      const result = await integrationsServiceV2.testConnection(selectedItem.connection.id, testCardNumber);
      toast.success("Test successful!", { description: <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4"><code className="text-white">{JSON.stringify(result, null, 2)}</code></pre> });
    } catch (error) {
      toast.error("Test Failed", { description: error.message });
    } finally {
      setIsTesting(false);
    }
  };

  const statusInfo = {
    CONNECTED: { text: "Connected", icon: CheckCircle, color: "text-green-500", bg: "bg-green-50" },
    ERROR: { text: "Connection Error", icon: XCircle, color: "text-red-500", bg: "bg-red-50" },
    DISABLED: { text: "Disabled", icon: AlertTriangle, color: "text-yellow-500", bg: "bg-yellow-50" },
    NOT_CONNECTED: { text: "Not Connected", icon: Unplug, color: "text-gray-500", bg: "bg-gray-100" },
  };

  const currentStatus = statusInfo[selectedItem.status] || statusInfo.NOT_CONNECTED;
  const StatusIcon = currentStatus.icon;

  const renderConnectionForm = () => {
    const authConfig = typeof selectedItem.auth_config === 'string' 
      ? JSON.parse(selectedItem.auth_config) 
      : selectedItem.auth_config;

    if (!authConfig || !authConfig.fields) return null;

    const isButtonDisabled = isLoading || isConnecting;

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        <div className="p-6 border-b">
          <h3 className="font-medium text-gray-900">Enter Credentials</h3>
          <p className="text-sm text-gray-500 mt-1">Provide the necessary details to activate this integration.</p>
        </div>
        <div className="p-6 space-y-4">
          {authConfig.fields.map(field => (
            <div key={field.name} className="space-y-2">
              <Label htmlFor={field.name} className="text-sm font-medium text-gray-700">{field.label}</Label>
              <Input
                id={field.name}
                name={field.name}
                type={field.type}
                placeholder={field.placeholder}
                required={field.required}
                value={formData[field.name] || ''}
                onChange={handleInputChange}
                autoComplete={field.type === 'password' ? 'new-password' : 'off'}
                className="w-full border-gray-200 rounded-xl h-11 px-4 text-sm focus:ring-2 focus:ring-black/50"
                disabled={isButtonDisabled}
              />
            </div>
          ))}
        </div>
        <div className="px-6 py-4 bg-gray-50/70 flex justify-end">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleConnectClick}
            disabled={isButtonDisabled}
            className="py-2.5 px-5 rounded-xl bg-black text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isButtonDisabled ? "Connecting..." : "Connect"}
          </motion.button>
        </div>
      </div>
    );
  };

  const renderConnectionDetails = () => (
    <div className="space-y-6">
      {/* Capabilities */}
      {isLoadingCapabilities && <p>Loading capabilities...</p>}
      {capabilities.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="p-6 border-b">
            <div className="flex items-center gap-3">
              <ServerCog className="w-5 h-5 text-gray-500" />
              <div>
                <h3 className="font-medium text-gray-900">Capabilities</h3>
                <p className="text-sm text-gray-500 mt-1">This integration enables the following abilities.</p>
              </div>
            </div>
          </div>
          <ul className="divide-y divide-gray-100">
            <AnimatePresence>
              {capabilities.map((cap) => {
                const firstHighlightStepIndex = cap.example_conversation?.steps.findIndex(step => /\[[^\]]+\]/.test(step.message)) ?? -1;

                return (
                  <motion.li key={cap.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <button 
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50/70"
                      onClick={() => setExpandedCapability(expandedCapability === cap.id ? null : cap.id)}
                    >
                      <div>
                        <p className="font-medium text-sm text-gray-800">{cap.name}</p>
                        <p className="text-sm text-gray-500">{cap.description}</p>
                      </div>
                      <motion.div animate={{ rotate: expandedCapability === cap.id ? 90 : 0 }}>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </motion.div>
                    </button>
                    <AnimatePresence>
                      {expandedCapability === cap.id && (
                        <motion.div
                          layout
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="bg-gray-50/70 border-t border-gray-200/80 overflow-hidden"
                        >
                          <div className="p-6">
                            <div className="mb-6">
                              <h4 className="text-sm font-medium text-gray-900 mb-1">{cap.example_conversation.title || `Example: ${cap.name}`}</h4>
                              <p className="text-xs text-gray-500">See how this capability works in a real conversation</p>
                            </div>

                            <div className="space-y-3 mb-6">
                              {cap.example_conversation.steps.map((step, stepIndex) => (
                                <div key={stepIndex} className={`flex items-start gap-3 ${step.actor === 'user' ? 'justify-end' : ''}`}>
                                  {step.actor === 'assistant' && (
                                    <div 
                                      className="w-8 h-8 shrink-0 p-2 rounded-lg flex items-center justify-center bg-gray-200/80 group relative"
                                      title="AI Assistant"
                                    >
                                      <AndroidLogo className="w-4 h-4 text-gray-600" />
                                    </div>
                                  )}
                                  {step.actor === 'action' && (
                                    <div 
                                      className={`w-8 h-8 shrink-0 p-2 rounded-lg flex items-center justify-center group relative ${
                                        step.type === 'answer' ? 'bg-purple-100' : 'bg-amber-100'
                                      }`}
                                      title={`${step.type === 'answer' ? 'Retrieving' : 'Executing'}: ${step.name}`}
                                    >
                                      {step.type === 'answer' ? (
                                        <Database className="w-4 h-4 text-purple-600" />
                                      ) : (
                                        <Wrench className="w-4 h-4 text-amber-600" />
                                      )}
                                    </div>
                                  )}

                                  <div className={`px-3 py-2 rounded-xl max-w-[75%] shadow-sm ${
                                    step.actor === 'user' ? "bg-black text-white" : "bg-white text-gray-900 border border-gray-200/80"
                                  }`}>
                                    {step.actor === 'action' ? (
                                      <div className="flex items-center gap-2">
                                        <p className="text-sm font-medium">{step.name}</p>
                                        <div className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">{step.status}</div>
                                      </div>
                                    ) : (
                                      <div className="text-sm font-light leading-relaxed">
                                        <HighlightedMessage
                                          message={step.message}
                                          isTriggerStep={stepIndex === firstHighlightStepIndex}
                                        />
                                      </div>
                                    )}
                                  </div>

                                  {step.actor === 'user' && (
                                    <div 
                                      className="w-8 h-8 shrink-0 p-2 rounded-lg flex items-center justify-center bg-black group relative"
                                      title="User"
                                    >
                                      <User className="w-4 h-4 text-white" />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {/* "What You'll Be Asked" section is here now */}
                            <div className="border-t border-gray-200/60 pt-6">
                              <div className="flex items-center justify-between mb-4">
                                <h5 className="text-sm font-medium text-gray-700">What You'll Be Asked</h5>
                              </div>

                              {cap.integration_tools && cap.integration_tools.length > 0 && cap.integration_tools[0].integration_tool_parameters ? (
                                <div className="space-y-3">
                                  {/* Required Information */}
                                  {cap.integration_tools[0].integration_tool_parameters.filter(p => p.required && !p.name.toLowerCase().includes('id') && !p.name.toLowerCase().includes('token')).length > 0 && (
                                    <div className="rounded-xl border border-gray-100 overflow-hidden">
                                      <div className="px-4 py-3 bg-white border-b border-gray-100">
                                        <h6 className="text-xs font-medium text-gray-600">Required</h6>
                                      </div>
                                      <div className="divide-y divide-gray-100">
                                        {cap.integration_tools[0].integration_tool_parameters
                                          .filter(p => p.required && !p.name.toLowerCase().includes('id') && !p.name.toLowerCase().includes('token'))
                                          .map(param => (
                                            <div key={param.id} className="px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
                                              <div className="text-sm">
                                                <span className="font-medium text-gray-800">{param.name}</span>
                                                <span className="text-gray-500 font-light ml-2">{param.description}</span>
                                              </div>
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Optional Information */}
                                  {cap.integration_tools[0].integration_tool_parameters.filter(p => !p.required && !p.name.toLowerCase().includes('id') && !p.name.toLowerCase().includes('token')).length > 0 && (
                                    <div className="rounded-xl border border-gray-100 overflow-hidden">
                                      <div className="px-4 py-3 bg-white border-b border-gray-100">
                                        <h6 className="text-xs font-medium text-gray-600">Optional</h6>
                                      </div>
                                      <div className="divide-y divide-gray-100">
                                        {cap.integration_tools[0].integration_tool_parameters
                                          .filter(p => !p.required && !p.name.toLowerCase().includes('id') && !p.name.toLowerCase().includes('token'))
                                          .map(param => (
                                            <div key={param.id} className="px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
                                              <div className="text-sm">
                                                <span className="font-medium text-gray-800">{param.name}</span>
                                                <span className="text-gray-500 font-light ml-2">{param.description}</span>
                                              </div>
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="flex flex-col items-center justify-center py-8 px-4 rounded-xl border border-dashed border-gray-200 text-center">
                                  <Key size={20} className="text-gray-300 mb-2" />
                                  <p className="text-gray-500 text-sm">No questions asked</p>
                                  <p className="text-xs text-gray-400 mt-1">This capability works with the information already provided</p>
                                </div>
                              )}
                            </div>

                            {/* Tool-specific testing with separator */}
                            {cap.integration_tools && cap.integration_tools.length > 0 && (
                              <div className="mt-6 pt-6 border-t border-gray-200/60">
                                <div className="space-y-4">
                                  {cap.integration_tools.map(tool => (
                                    <ToolTestSection 
                                      key={tool.id} 
                                      connectionId={selectedItem.connection.id} 
                                      tool={tool} 
                                      capabilityName={cap.name}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.li>
                );
              })}
            </AnimatePresence>
          </ul>
        </div>
      )}

      {/* Danger Zone */}
      <div className="bg-red-50/70 rounded-2xl border border-red-200/80 p-6 flex items-center justify-between">
        <div>
          <h3 className="font-medium text-red-900">Disconnect Integration</h3>
          <p className="text-sm text-red-700/90 mt-1">This will immediately stop all data flow.</p>
        </div>
        <Button onClick={() => setIsConfirmModalOpen(true)} disabled={isLoading} variant="destructive">
          {isLoading ? 'Disconnecting...' : 'Disconnect'}
        </Button>
      </div>
    </div>
  );

  const renderErrorState = () => (
    <div className="bg-white rounded-2xl shadow-sm border border-red-200/50 p-6 text-center">
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-red-900">Connection Error</h3>
        <p className="mt-2 text-sm text-red-700">There was a problem with this integration. Please try disconnecting and connecting again.</p>
        <div className="mt-6">
            <Button onClick={() => setIsConfirmModalOpen(true)} disabled={isLoading} variant="destructive">
                {isLoading ? 'Disconnecting...' : 'Disconnect Integration'}
            </Button>
        </div>
    </div>
  )

  return (
    <>
    <div className="flex flex-col h-full bg-gray-50/50">
      {/* Header */}
      <div className="p-6 border-b border-gray-200/80 bg-white">
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4 max-w-[66%]">
                {selectedItem.logo_url && (
                  <div className="p-2 bg-gray-100/80 rounded-lg shrink-0">
                    <img src={selectedItem.logo_url} alt={`${selectedItem.name} icon`} className="w-7 h-7" />
                  </div>
                )}
                <div>
                    <h2 className="font-medium text-lg text-gray-900 truncate">{selectedItem.name}</h2>
                    <p className="text-sm text-gray-500 mt-0.5">{selectedItem.description}</p>
                </div>
            </div>
            <div className={`inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${currentStatus.bg} ${currentStatus.color} w-44`}>
                <StatusIcon className="w-4 h-4" />
                <span>{currentStatus.text}</span>
            </div>
        </div>
      </div>

      {/* Content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedItem.status}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {selectedItem.status === 'CONNECTED' && renderConnectionDetails()}
            {selectedItem.status === 'NOT_CONNECTED' && renderConnectionForm()}
            {selectedItem.status === 'ERROR' && renderErrorState()}
            {selectedItem.status === 'DISABLED' && renderErrorState()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
    <ConfirmModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDisconnectClick}
        title="Disconnect Integration"
        message={`Are you sure you want to disconnect ${selectedItem.name}? This action cannot be undone.`}
        confirmText="Yes, Disconnect"
        confirmStyle="bg-red-600 hover:bg-red-700"
    />
    </>
  );
};

export default IntegrationV2Content; 